import React from 'react';
import Lozenge from '@atlaskit/lozenge';
import Tooltip from '@atlaskit/tooltip';

import {
  flexCenter,
  flexDirectionColumn,
  flexStart,
  spaceGapTiny,
  tableHeadStyles,
} from '@/styles';
import { DateDisplayFormats } from '@/constants';
import { formatDateForDisplay } from '@/utils';
import { ExternalLinkAction } from '@/components/primitive';

import SfxIcon from '../sfx-icon';

export interface Detector {
  detectorId: string;
  teamPublicId?: string;
  alertType: string;
  status: string;
  // Comma-separated Opsgenie team IDs found on the detector's rules
  recipients?: string | null;
  detectorUpdatedTimestamp: string;
  detectorCreatedTimestamp: string;
  detectorVersion: string;
}

interface DetectorsTableProps {
  detectors: Detector[];
  owningTeamOpsgenieId?: string;
  title?: string;
}

// Classic burn-rate alert types have no "burn" in their name; other kinds (BREACH, CTD_ERROR_RATE, ...) do not get it.
const BURN_RATE_ALERT_TYPES = new Set(['GRADUAL', 'MODERATE', 'STEEP', 'ULTRA_STEEP']);

export const alertTypeLabel = (alertType: string): string =>
  BURN_RATE_ALERT_TYPES.has(alertType) ? `${alertType} burn` : alertType;

export const splitRecipients = (recipients?: string | null): string[] =>
  (recipients ?? '')
    .split(',')
    .map(r => r.trim())
    .filter(Boolean);

interface RecipientsCellProps {
  detector: Detector;
  owningTeamOpsgenieId?: string;
}

const RecipientsCell: React.FC<RecipientsCellProps> = ({ detector, owningTeamOpsgenieId }) => {
  const recipients = splitRecipients(detector.recipients);
  const isOwningTeam = (id: string) => Boolean(owningTeamOpsgenieId) && id === owningTeamOpsgenieId;
  const ordered = [
    ...recipients.filter(isOwningTeam),
    ...recipients.filter(id => !isOwningTeam(id)),
  ];
  return (
    <div css={[flexStart, flexDirectionColumn, spaceGapTiny]}>
      {ordered.length === 0 && (
        <Lozenge appearance="removed" isBold={false}>
          None
        </Lozenge>
      )}
      {ordered.map(id => {
        if (isOwningTeam(id)) {
          return (
            <Tooltip key={id} content={id}>
              <Lozenge appearance="inprogress" isBold={false}>
                Owning Team
              </Lozenge>
            </Tooltip>
          );
        }
        return (
          <Lozenge key={id} appearance="default" isBold={false} maxWidth="none">
            {id}
          </Lozenge>
        );
      })}
    </div>
  );
};

const DetectorsTable: React.FC<DetectorsTableProps> = ({
  detectors,
  owningTeamOpsgenieId,
  title,
}) => (
  <div>
    {title && <h4>{title}</h4>}
    <table>
      <thead css={tableHeadStyles}>
        <tr>
          <th>Detector ID</th>
          <th>Alert Type</th>
          <th>Status</th>
          <th>Recipients</th>
          <th>Created At (UTC)</th>
          <th>Updated At (UTC)</th>
          <th>Version</th>
          <th css={[flexCenter, spaceGapTiny]}>
            <SfxIcon />
            <span>Link</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {detectors.map(detector => {
          const {
            detectorId,
            alertType,
            status,
            detectorUpdatedTimestamp,
            detectorCreatedTimestamp,
            detectorVersion,
          } = detector;
          return (
            <tr key={detectorId + alertType}>
              <td>{detectorId}</td>
              <td>
                <Lozenge appearance="default" isBold={false}>
                  {alertTypeLabel(alertType)}
                </Lozenge>
              </td>
              <td>
                <Lozenge appearance={status === 'HEALTHY' ? 'success' : 'default'} isBold={false}>
                  {status}
                </Lozenge>
              </td>
              <td>
                <RecipientsCell detector={detector} owningTeamOpsgenieId={owningTeamOpsgenieId} />
              </td>
              <td>
                <code>
                  {formatDateForDisplay(
                    detectorCreatedTimestamp,
                    DateDisplayFormats.TIME_WITH_NUMERIC_DATE
                  )}
                </code>
              </td>
              <td>
                <code>
                  {formatDateForDisplay(
                    detectorUpdatedTimestamp,
                    DateDisplayFormats.TIME_WITH_NUMERIC_DATE
                  )}
                </code>
              </td>
              <td>{detectorVersion}</td>
              <td>
                <ExternalLinkAction
                  text="View"
                  href={`https://atlassian.signalfx.com/#/detector/v2/${detectorId}/edit`}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default DetectorsTable;
